import React, { useCallback, useMemo } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import { JollyRancherChip } from "veneer/Chip";
import DataTable, { createColumnHelper } from "veneer/DataTable";
import {
  ContractIconButton,
  DeleteIconButton,
  ExpandIconButton,
} from "veneer/IconButton";
import { Colors } from "veneer/Theme";
import Typography, { OverflowTooltipTypography } from "veneer/Typography";

import { SIDEBAR_EDIT_CONTENT_KEY } from "cortex/ui/src/fleet/constant/sidebar";
import {
  changePriorityOrderAction,
  deleteSpotAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/strategy/actions";
import {
  getArchiveStatus,
  isVideo,
} from "cortex/ui/src/fleet/view/media-library/utils";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { setActiveKeySidebarAction } from "cortex/ui/src/redux-store/ui-state/sidebar/actions";
import { ContentArchived } from "cortex/ui/src/ui/content-archived";
import { formatSpotPreview } from "cortex/ui/src/util/tableFormat";
import { durationToHuman } from "cortex/ui/src/util/time";

import { Spot, Strategy } from "../model";

const baseStorePath = "pages.strategyDetail.strategy";
type StrategyPriority = "main" | "fallback";

interface SpotTableProps {
  priority: StrategyPriority;
  rows: ReadonlyArray<Spot>;
  strategy: Strategy;
}

const column = createColumnHelper<Spot>();

export default function SpotTable({ priority, rows = [] }: SpotTableProps) {
  const dispatch = useAppDispatch();
  const rowCount = rows.length;

  const handleChangePriorityOrder = useCallback(
    (dir: -1 | 1, row: Spot) => {
      dispatch(
        changePriorityOrderAction({ dir, index: row._rowNum, priority }),
      );
    },
    [dispatch, priority],
  );

  const handleDeleteSpot = useCallback(
    (row: Spot) => {
      dispatch(deleteSpotAction({ index: row._rowNum, priority }));
    },
    [dispatch, priority],
  );

  const handleShowSidebar = useCallback(
    (row: Spot) => {
      const rowIdx = row._rowNum;
      const storePath = `${baseStorePath}.${priority}.${rowIdx}`;
      dispatch(
        setActiveKeySidebarAction(
          SIDEBAR_EDIT_CONTENT_KEY,
          "File Details",
          {},
          storePath,
        ),
      );
    },
    [dispatch, priority],
  );

  const columns = useMemo(
    () => [
      column.accessor("_rowNum", {
        header: "Order",
        cell: (prop) => prop.getValue() + 1,
        enableSorting: false,
        meta: {
          align: "center",
          width: 60,
        },
      }),
      column.accessor("url", {
        header: "Preview",
        cell: (prop) => formatSpotPreview(null, prop.row.original),
        enableSorting: false,
        meta: {
          align: "center",
          width: 125,
        },
      }),
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const name = prop.getValue();
          const row = prop.row.original;
          if (row.type === "application" && !row.available) {
            return "";
          }
          return (
            <>
              <OverflowTooltipTypography variant="body2">
                {name}
              </OverflowTooltipTypography>
              <AppUnavailable mediaEntry={row} />
              <ContentArchived
                mediaEntry={
                  row as Parameters<typeof ContentArchived>[0]["mediaEntry"]
                }
              />
            </>
          );
        },
        enableSorting: false,
        meta: {
          primary: true,
        },
      }),
      column.accessor("duration", {
        header: "Duration",
        cell: (prop) => {
          const duration = prop.getValue();
          return isVideo(prop.row.original.mimeType)
            ? "N/A"
            : durationToHuman(duration);
        },
        enableSorting: false,
        meta: {
          width: 60,
        },
      }),
      column.accessor("friendlyType", {
        header: "Type",
        cell: (prop) => prop.getValue(),
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      column.accessor("tag", {
        header: "Creative Category",
        cell: (prop) => {
          const tag = prop.getValue();
          if (!tag) {
            return null;
          }
          return <JollyRancherChip color="red" label={tag} />;
        },
        enableSorting: false,
      }),
      column.display({
        id: "actions",
        header: "Actions",
        cell: (prop) => {
          const row = prop.row.original;
          return (
            <Box>
              <ExpandIconButton
                disabled={row._rowNum === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangePriorityOrder(-1, row);
                }}
              />
              <ContractIconButton
                disabled={row._rowNum === rowCount - 1}
                onClick={(e) => {
                  e.stopPropagation();
                  handleChangePriorityOrder(1, row);
                }}
              />
              <DeleteIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSpot(row);
                }}
              />
            </Box>
          );
        },
        meta: {
          width: 150,
        },
      }),
    ],
    [rowCount, handleChangePriorityOrder, handleDeleteSpot],
  );

  return (
    <Box mx={-2}>
      <DataTable
        columns={columns}
        emptyState={
          <Box p={2} textAlign="center">
            <Typography color={Colors.gray1000} variant="body2">
              There is no data to display
            </Typography>
          </Box>
        }
        getRowProps={(row) => ({
          hover: true,
          inactive: Boolean(
            getArchiveStatus(row as Parameters<typeof getArchiveStatus>[0]),
          ),
          onClick: () => handleShowSidebar(row),
        })}
        rows={rows}
      />
    </Box>
  );
}

interface AppUnavailableProps {
  mediaEntry: Spot;
}

const AppUnavailable = ({ mediaEntry }: AppUnavailableProps) => {
  const { displayName, friendlyType, installed, type, available } = mediaEntry;
  if (type !== "application") {
    return null;
  }

  if (installed && available) {
    return null;
  }

  // When we denormalize a strategy, a missing application in strategy is not
  // available in global applications store. Therefore it won't have an id in
  // mediaEntry.
  const title = available
    ? `${friendlyType} ${displayName} is not installed for your account.`
    : `This ${friendlyType} is no longer available in your account.`;

  return (
    <Alert dense severity="warning" title={title}>
      Please update your strategy.
    </Alert>
  );
};
